import React, { FC, JSX, MouseEvent } from 'react';
import { AvatarProps } from '../../types/components.d';

const Avatar: FC<AvatarProps> = ({ 
  src, 
  alt, 
  name, 
  size = 'md', 
  shape = 'circle',
  status,
  className = '',
  onClick 
}): JSX.Element => {
  const sizes: { [key: string]: string } = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl',
    '2xl': 'w-20 h-20 text-2xl',
  };

  const shapes = {
    circle: 'rounded-full',
    square: 'rounded-lg',
    rounded: 'rounded-xl',
  };

  const statusColors = {
    online: 'bg-green-500',
    offline: 'bg-gray-400',
    away: 'bg-yellow-500',
    busy: 'bg-red-500',
  };

  const getInitials = (name?: string): string => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getBackgroundColor = (name?: string): string => {
    if (!name) return 'bg-gray-400';
    
    const colors = [
      'bg-red-500',
      'bg-primary-lightGreen',
      'bg-green-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-primary-teaGreen',
      'bg-teal-500',
    ];
    
    const hash = name.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  const baseClasses = `inline-flex items-center justify-center font-semibold text-white ${shapes[shape]} ${sizes[size]} ${className}`;
  const clickableClasses = onClick ? 'cursor-pointer hover:scale-105 transition-transform duration-200' : '';

  return (
    <div className="relative inline-block">
      {src ? (
        <img
          src={src}
          alt={alt || name}
          className={`${baseClasses} ${clickableClasses} object-cover`}
          onClick={onClick}
        />
      ) : (
        <div
          className={`${baseClasses} ${getBackgroundColor(name)} ${clickableClasses}`}
          onClick={onClick}
        >
          {getInitials(name)}
        </div>
      )}
      
      {status && (
        <div
          className={`absolute bottom-0 right-0 w-3 h-3 ${statusColors[status]} border-2 border-white rounded-full`}
        />
      )}
    </div>
  );
};

export default Avatar;
